import { useState, useEffect } from "react";
import { EyeIcon } from "@heroicons/react/24/outline";

import {
    CheckIcon,
    TrashIcon
} from "@heroicons/react/24/outline";

import { IExcelArraySheet, SelectPagesMode } from "../lib/types";
import {
    classNames,
    getExcelColumnName
} from "../lib/utils";

import { TimeoutErrorMessageBar } from "./ErrorMessageBar";
import { FullScreen } from "./FullScreen";
import { Dropdown } from "./Dropdown";
import { Checkbox } from "./Checkbox";
import { IconButton } from "./Button";

type SheetProps = {
    data: string[][],
    show_header?: boolean
};

export function Sheet(props: SheetProps) {
    const { data, show_header } = props;

    let number_of_cols = 0;
    for (const row of data) {
        if (row.length > number_of_cols) {
            number_of_cols = row.length;
        }
    }

    if (data.length === 0) {
        return <span>This worksheet is empty.</span>;
    }

    const cols = Array.from({ length: number_of_cols }).map((_, idx) => idx);
    const first_rows = data.length <= 21 ? data.slice(show_header ? 1 : 0) : data.slice(show_header ? 1 : 0, 10);
    const last_rows = data.length <= 21 ? [] : data.slice(-10);

    return <table className="">
        <thead>
            <tr>
                <th className="min-w-[20px] bg-gray-100 border border-gray-200"></th>
                {cols.map((idx) =>
                    <th key={idx} className="font-normal min-w-[50px] max-w-[200px] bg-gray-100 border border-gray-200 text-xs">
                        {getExcelColumnName(idx)}
                    </th>
                )}
            </tr>
            {show_header && <tr>
                <th className="min-w-[20px] bg-sea_blue-100 border border-gray-200 font-normal text-xs px-1 text-center">Header</th>
                {cols.map((idx) =>
                    <th key={idx} className="min-w-[50px] max-w-[200px] bg-sea_blue-100 border border-gray-200 font-normal text-xs px-1 truncate tracking-tighter">
                        {data[0][idx] || ""}
                    </th>
                )}
            </tr>}
        </thead>
        <tbody>
            {first_rows.map((row, idx) =>
                <tr key={idx}>
                    <td className="min-w-[20px] bg-gray-100 border border-gray-200 font-normal text-xs px-1 text-center">{idx + (show_header ? 2 : 1)}</td>
                    {cols.map((col, idx) =>
                        <td key={idx} className="min-w-[50px] max-w-[200px] bg-white border border-gray-200 font-normal text-xs px-1 truncate tracking-tighter">
                            {row[col] || ""}
                        </td>
                    )}
                </tr>
            )}
            {last_rows.length > 0 && <tr>
                <td className="min-w-[20px] bg-gray-100 border border-gray-200 font-normal text-xs px-1 text-center text-">...</td>
                <td colSpan={number_of_cols} className="min-w-[20px] bg-gray-50 border border-gray-200 px-1"></td>
            </tr>}
            {last_rows.map((row, idx) =>
                <tr key={idx}>
                    <td className="min-w-[20px] bg-gray-100 border border-gray-200 font-normal text-xs px-1 text-center">{data.length - last_rows.length + idx + (show_header ? 2 : 1)}</td>
                    {cols.map((col, idx) =>
                        <td key={idx} className="min-w-[50px] max-w-[200px] bg-white border border-gray-200 font-normal text-xs px-1 truncate tracking-tighter">
                            {row[col] || ""}
                        </td>
                    )}
                </tr>
            )}
        </tbody>
    </table>;
}

type SheetsProps = {
    sheets: IExcelArraySheet[],
    selected_sheet_idx: number,
    setSelectedSheetIdx: (idx: number) => void
    show_header?: boolean
};

export function Sheets(props: SheetsProps) {
    const { sheets, selected_sheet_idx, setSelectedSheetIdx, show_header } = props;

    const selected_sheet = sheets[selected_sheet_idx];

    return <div className="flex flex-col gap-2">
        <div className="grid grid-cols-4 items-center w-full">
            <div className="text-sm text-gray-500">Sheet</div>
            <div className="col-span-3">
                <Dropdown
                    values={sheets.map((sheet) => sheet.name)}
                    ids={sheets.map((_, idx) => `${idx}`)}
                    selected={`${selected_sheet_idx}`}
                    onChange={(idx) => setSelectedSheetIdx(parseInt(idx, 10))} />
            </div>
        </div>
        <div className="w-full">
            <div className="outer-div">
                {selected_sheet && <Sheet data={selected_sheet.data} show_header={show_header} />}
            </div>
        </div>
    </div>;
}

type SheetsViewerProps = {
    sheets: IExcelArraySheet[],
    show_header?: boolean,
    enable_sheet_select?: boolean,
    select_pages_mode?: SelectPagesMode,
    notifySelectedPages?: (selected_pages: number[], page_delimiters: number[]) => void
};

export function SheetsViewer(props: SheetsViewerProps) {
    const { sheets, show_header, enable_sheet_select, select_pages_mode, notifySelectedPages } = props;

    const [selected_sheets, setSelectedSheets] = useState<number[]>([]);
    const [modal_open, setModalOpen] = useState<boolean>(false);
    const [modal_sheet_idx, setModalSheetIdx] = useState<number>(0);
    const [error_message, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (enable_sheet_select) {
            let sheets_to_select: number[] = [];

            if (!select_pages_mode || select_pages_mode.type === "all") {
                sheets_to_select = Array.from({ length: sheets.length }, (_, i) => i);
            } else if (select_pages_mode.type === "first_page" && sheets.length > 0) {
                sheets_to_select = [0];
            } else if (select_pages_mode.type === "last_page" && sheets.length > 0) {
                sheets_to_select = [sheets.length - 1];
            }

            setSelectedSheets(sheets_to_select);
            if (notifySelectedPages) { notifySelectedPages(sheets_to_select, []); }
        }
    }, [sheets, enable_sheet_select, select_pages_mode]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSheetSelection = (idx: number) => {
        if (!enable_sheet_select) {
            return;
        }

        const newSelection = [...selected_sheets];
        const index = newSelection.indexOf(idx);

        if (index >= 0) {
            newSelection.splice(index, 1);
        } else {
            newSelection.push(idx);
            newSelection.sort((a, b) => a - b);
        }

        if (newSelection.length >= 1) {
            setSelectedSheets(newSelection);
            if (notifySelectedPages) { notifySelectedPages(newSelection, []); }
        } else {
            setErrorMessage("At least one sheet must be selected");
        }
    };

    const openSheetModal = (idx: number, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        setModalSheetIdx(idx);
        setModalOpen(true);
    };

    const renderSheetPreview = (sheet: IExcelArraySheet, idx: number) => {
        const previewRows = Math.min(sheet.data.length, 30);
        const previewCols = sheet.data.length > 0 ? Math.max(...sheet.data.slice(0, previewRows).map(row => row.length)) : 0;

        const isSelected = selected_sheets.includes(idx);

        return (
            <div key={idx} className="flex flex-col items-center">
                <div
                    className={classNames(
                        "relative border rounded overflow-hidden cursor-pointer transition-all mb-2 h-48 w-full",
                        isSelected ? "" : "opacity-60"
                    )}
                    onClick={() => toggleSheetSelection(idx)}
                >
                    <div className="bg-gray-50 px-2 py-0.5 border-b text-sm font-medium truncate flex justify-between items-center">
                        <div className="truncate mr-6">{sheet.name}</div>
                        <IconButton
                            icon={EyeIcon}
                            onClick={() => openSheetModal(idx)}
                        />
                    </div>

                    <div className="p-0.5 bg-white h-full overflow-hidden">
                        <table className="w-full border-collapse">
                            <tbody>
                                {Array.from({ length: previewRows }).map((_, rowIdx) => (
                                    <tr key={rowIdx}>
                                        {Array.from({ length: previewCols }).map((_, colIdx) => (
                                            <td key={colIdx} className="border border-gray-200 p-0.5 truncate max-w-[60px] text-[0.65rem] leading-tight">
                                                {sheet.data[rowIdx]?.[colIdx] || ""}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {enable_sheet_select && (
                    <div className="flex justify-center mb-4">
                        <Checkbox
                            id={`sheet-select-${idx}`}
                            checked={isSelected}
                            setChecked={() => toggleSheetSelection(idx)}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-3">
            <div className={classNames(
                "grid gap-4",
                sheets.length === 1 ? "grid-cols-1" : "",
                sheets.length === 2 ? "grid-cols-2" : "",
                sheets.length === 3 ? "grid-cols-3" : "",
                sheets.length > 3 ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "")}>
                {sheets.map((sheet, idx) => renderSheetPreview(sheet, idx))}
            </div>

            <FullScreen
                show={modal_open}
                onClose={() => setModalOpen(false)}
            >
                <div className="p-4 rounded-lg w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
                        {sheets[modal_sheet_idx]?.name}
                    </h3>
                    <div className="w-full overflow-auto max-h-[70vh]">
                        <div className="outer-div">
                            {sheets[modal_sheet_idx] && (
                                <Sheet data={sheets[modal_sheet_idx].data} show_header={show_header} />
                            )}
                        </div>
                    </div>
                </div>
            </FullScreen>
            <TimeoutErrorMessageBar message={error_message} clearMessage={() => setErrorMessage(undefined)} />
        </div>
    );
}

type SheetRow = {
    uuid: string,
    row: string[]
}

type SheetsEditorProps = {
    headers: string[],
    rows: SheetRow[],
    onAddRow: (row: string[]) => Promise<void>,
    onDeleteRow: (uuid: string) => Promise<void>,
    onUpdateRow: (uuid: string, row: string[]) => Promise<void>
}

export function SheetEditor(props: SheetsEditorProps) {
    const { headers, rows, onAddRow, onDeleteRow, onUpdateRow } = props;

    const [row_changes, setRowChanges] = useState<{ row_idx: number, data: string[] }[]>([]);
    const [new_row, setNewRow] = useState<string[]>(Array.from({ length: headers.length }).map(() => ""));
    const [is_new_row_changed, setIsNewRowChanged] = useState<boolean>(false);

    const handleOldRowInput = (row_idx: number) => {
        if (row_changes.find((change) => change.row_idx === row_idx)) {
            return;
        }
        const row_copy = [...rows[row_idx].row];
        setRowChanges([...row_changes, { row_idx, data: row_copy }]);
    };

    const handleOldRowBlur = (e: any, row_idx: number, col_idx: number) => {
        const value = e.target.innerText.trim();
        const row_copy = row_changes.find((change) => change.row_idx === row_idx)?.data || rows[row_idx].row;
        row_copy[col_idx] = value;
        setRowChanges(row_changes.map((change) => change.row_idx === row_idx ? { row_idx, data: row_copy } : change));
    };

    const handleOldRowUpdate = (row_idx: number) => {
        const row = row_changes.find((change) => change.row_idx === row_idx)?.data || rows[row_idx].row;
        onUpdateRow(rows[row_idx].uuid, row);
        setRowChanges(row_changes.filter((change) => change.row_idx !== row_idx));
    };

    const handleNewRowInput = () => {
        setIsNewRowChanged(true);
    };

    const handleNewRowBlur = (e: any, col_idx: number) => {
        const value = e.target.innerText.trim();
        const new_row_copy = [...new_row];
        new_row_copy[col_idx] = value;
        setNewRow(new_row_copy);
    };

    const handleNewRowAdd = () => {
        onAddRow(new_row);
        setNewRow(Array.from({ length: headers.length }).map(() => ""));
        setIsNewRowChanged(false);
    }

    const edit_row_idx_set = new Set(row_changes.map((change) => change.row_idx));

    return <div>
        <table>
            <thead>
                <tr>
                    <th className="min-w-[20px] bg-gray-50 border border-gray-200"></th>
                    {headers.map((_header, idx) =>
                        <th key={idx} className="font-normal min-w-[50px] max-w-[200px] bg-gray-50 border border-gray-200 px-2 py-1 text-xs">
                            {getExcelColumnName(idx)}
                        </th>
                    )}
                </tr>
                <tr>
                    <th className="min-w-[20px] bg-gray-100 border border-gray-200"></th>
                    {headers.map((header, idx) =>
                        <th key={idx} title={header} className="truncate font-normal min-w-[50px] max-w-[200px] bg-gray-100 border border-gray-200 px-2 py-1 text-sm">
                            {header}
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, row_idx) =>
                    <tr key={row_idx}>
                        <td className="min-w-[20px] bg-gray-100 border border-gray-200 font-normal px-2 py-1 text-sm text-center">{row_idx + 1}</td>
                        {row.row.map((col, col_idx) =>
                            <td key={col_idx}
                                className={classNames(
                                    "min-w-[50px] max-w-[200px] bg-white border border-gray-200 font-normal px-2 py-1 text-sm truncate tracking-tighter",
                                    edit_row_idx_set.has(row_idx) ? "font-semibold" : "font-normal"
                                )}
                                contentEditable={true}
                                onInput={() => handleOldRowInput(row_idx)}
                                onBlur={(e) => handleOldRowBlur(e, row_idx, col_idx)}
                                dangerouslySetInnerHTML={{ __html: col }}
                            />
                        )}
                        {!edit_row_idx_set.has(row_idx) && <td
                            className="p-2 w-4 align-middle bg-gray-100 text-gray-600 border border-gray-200 cursor-pointer hover:text-white hover:bg-sea_blue-300"
                            onClick={() => onDeleteRow(row.uuid)}
                        >
                            <TrashIcon className="w-4 h-4" />
                        </td>}
                        {edit_row_idx_set.has(row_idx) && <td
                            className="py-1 px-2 bg-gray-100 border border-gray-200 text-center text-sm text-gray-600 align-middle cursor-pointer hover:text-white hover:bg-sea_blue-300"
                            onClick={() => handleOldRowUpdate(row_idx)}
                        >
                            <CheckIcon className="w-4 h-4" />
                        </td>}
                    </tr>
                )}
                <tr>
                    <td className="min-w-[20px] bg-gray-100 border border-gray-200 font-normal px-2 py-1 text-sm text-center h-7" />
                    {new_row.map((col, col_idx) =>
                        <td
                            key={col_idx} className="min-w-[50px] max-w-[200px] bg-white border border-gray-200 font-normal px-2 py-1 text-sm truncate tracking-tighter"
                            contentEditable={true}
                            onInput={(e) => handleNewRowInput()}
                            onBlur={(e) => handleNewRowBlur(e, col_idx)}
                            dangerouslySetInnerHTML={{ __html: col }} />
                    )}
                    {is_new_row_changed && <td
                        className="py-1 px-2 bg-gray-100 border border-gray-200 text-center text-sm text-gray-600 align-middle cursor-pointer hover:text-white hover:bg-sea_blue-300"
                        onClick={() => handleNewRowAdd()}
                    >
                        <CheckIcon className="w-4 h-4" />
                    </td>}
                </tr>

            </tbody>
        </table>
    </div>

}